import NavBar from "./NavBar";
import { UserContext } from "./UserContext";
import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import {
  Box,
  Button,
  Text,
  VStack,
  Heading,
  Flex,
  Tooltip,
  keyframes,
} from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { workflowLaunchCache } from "../services/WorkflowLaunchCache";
import DemoVideo from "./DemoVideo";
import TextareaAutosize from "react-textarea-autosize";
import { KnowledgePopover } from "./ChatFooterBar";
import chatStateManager from "../services/ChatStateManager";
import EmbeddedChatPanel from "./EmbeddedChatPanel";
import BriefProjectsPreview from "./BriefProjectsPreview";
import BriefClientSetup from "./client/BriefClientSetup";
import { InstallDisclosure } from "./client/InstallDisclosure";
import { useClientInstallConfig, clientInstallAvailable } from "./client/ClientInstallInstructions";
import "./FloatingChatbot.css";

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

// The two ways into Pabs. Someone already building brings Pabs into their
// tool; someone with an idea talks it through first.
const ENTRY_CHOICES = [
  {
    id: "building",
    label: "I’m already building",
    description: "You have code, or a project in an AI builder like Lovable or Replit. Bring Pabs into that tool.",
  },
  {
    id: "idea",
    label: "I have an idea",
    description: "Nothing built yet. Talk it through with Pabs before you start.",
  },
];

// An idea opens the chat for discovery. A product already being built goes
// straight to plan creation as a Quick Strategy.
const COMPOSER_COPY = {
  idea: {
    label: "Describe your idea",
    prompt: "I have this idea: [describe it, the problem you think exists, who has it, and why now]",
    hint: "Pabs asks a few questions first, then turns the conversation into a strategy and plan when you’re ready.",
    action: "Talk it through",
    actionSuffix: " with Pabs",
  },
  describe: {
    label: "Describe your product",
    prompt: "We're building [describe what it is, who it's for, the problem it solves] and I want to [what you're questioning or need clarity on]",
    hint: "Pabs works without a connection, but it only knows what you tell it.",
    action: "Create my plan",
    actionSuffix: "",
  },
};

const BRIEF_PLAN_WORKFLOW = { id: "quick_plan", name: "Quick Strategy" };

function EntryChoice({ value, onChange }) {
  const selectedIndex = ENTRY_CHOICES.findIndex((choice) => choice.id === value);
  // The white pill stays where it was while it fades out, so it never jumps.
  const lastIndex = useRef(Math.max(selectedIndex, 0));
  if (selectedIndex >= 0) lastIndex.current = selectedIndex;

  // Tooltips open on hover or keyboard focus, and close on leave or click.
  // After a click the tooltip stays closed until the pointer leaves, so the
  // focus and hover events that follow a click can't reopen it.
  const [tooltipFor, setTooltipFor] = useState(null);
  const tooltipTimer = useRef(null);
  const dismissedFor = useRef(null);
  const showTooltip = (id) => {
    if (dismissedFor.current === id) return;
    clearTimeout(tooltipTimer.current);
    tooltipTimer.current = setTimeout(() => setTooltipFor(id), 150);
  };
  const hideTooltip = () => {
    clearTimeout(tooltipTimer.current);
    setTooltipFor(null);
  };
  useEffect(() => () => clearTimeout(tooltipTimer.current), []);

  return (
    <Box
      role="group"
      aria-label="Where your product is at"
      position="relative"
      display="grid"
      gridTemplateColumns="repeat(2, minmax(0, 1fr))"
      gap="4px"
      p="4px"
      bg="gray.900"
      borderRadius="full"
      w={{ base: "100%", sm: "auto" }}
    >
      <Box
        aria-hidden="true"
        position="absolute"
        top="4px"
        bottom="4px"
        left="4px"
        w="calc(50% - 6px)"
        borderRadius="full"
        bg="white"
        opacity={selectedIndex < 0 ? 0 : 1}
        transform={lastIndex.current === 1 ? "translateX(calc(100% + 4px))" : "translateX(0)"}
        transition="transform 260ms cubic-bezier(.16, 1, .3, 1), opacity 160ms ease"
        sx={{ "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
      />
      {ENTRY_CHOICES.map((choice) => {
        const active = choice.id === value;
        return (
          <Tooltip
            key={choice.id}
            label={choice.description}
            isOpen={tooltipFor === choice.id}
            hasArrow
            placement="bottom"
            maxW="260px"
            px={3}
            py={2}
            borderRadius="md"
          >
            <Box
              as="button"
              type="button"
              aria-pressed={active}
              onMouseEnter={() => showTooltip(choice.id)}
              onMouseLeave={() => {
                dismissedFor.current = null;
                hideTooltip();
              }}
              onFocus={(event) => {
                if (event.currentTarget.matches(":focus-visible")) showTooltip(choice.id);
              }}
              onBlur={() => {
                dismissedFor.current = null;
                hideTooltip();
              }}
              onKeyDown={(event) => {
                if (event.key === "Escape") hideTooltip();
              }}
              onClick={() => {
                dismissedFor.current = choice.id;
                hideTooltip();
                onChange(choice.id);
              }}
              position="relative"
              zIndex={1}
              h="44px"
              px={{ base: 3, sm: 6 }}
              borderRadius="full"
              fontSize={{ base: "sm", sm: "md" }}
              fontWeight="semibold"
              whiteSpace="nowrap"
              color={active ? "gray.800" : "gray.400"}
              _hover={{ color: active ? "gray.800" : "gray.200" }}
              transition="color 0.15s ease"
              _focusVisible={{ outline: "2px solid", outlineColor: "blue.300", outlineOffset: "2px" }}
            >
              {choice.label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function ClientBriefForm() {
  const {
    userDetails,
    chatbotRef,
    isChatCollapsed,
    setIsChatCollapsed,
    setCurrentPlanId,
  } = useContext(UserContext);
  const { conversationId } = useParams(); // Get conversation ID from URL
  const { state: locationState, search } = useLocation();
  const navigate = useNavigate();
  const { checkLimit } = usePlanLimits();
  const planSubmitInFlight = useRef(false);

  const [leftPanelWidth] = useState(415); // Fixed width like PlanPage

  // Clear stale plan context when landing on brief page
  useEffect(() => {
    if (setCurrentPlanId) setCurrentPlanId(null);
    if (locationState?.openChat) setIsChatCollapsed(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [isDemoOpen, setIsDemoOpen] = useState(false); // State for demo video modal
  const [isFetchingDemoStatus, setIsFetchingDemoStatus] = useState(true); // Loading state for the API call

  // Fetch the `has_seen_demo` status and show the demo video if needed
  useEffect(() => {
    const fetchDemoStatus = async () => {
      try {
        // Check if we have a client-side flag first (backup mechanism)
        const localHasSeenDemo = localStorage.getItem("hasSeenDemo");
        if (localHasSeenDemo === "true") {
          setIsDemoOpen(false);
          setIsFetchingDemoStatus(false);
          return;
        }

        // Otherwise, fetch from server
        const response = await fetchWithAuth(`/api/user/demo-status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setIsDemoOpen(!data.hasSeenDemo); // Show the video only if `hasSeenDemo` is false

          // Set client-side flag if they've seen the demo
          if (data.hasSeenDemo) {
            localStorage.setItem("hasSeenDemo", "true");
          }
        } else {
          console.error("Failed to fetch demo status:", response.status);
          // Default to showing the demo if we can't fetch status
          setIsDemoOpen(true);
        }
      } catch (error) {
        console.error("Error fetching demo status:", error);
        // Default to showing the demo if there's an error
        setIsDemoOpen(true);
      } finally {
        setIsFetchingDemoStatus(false); // Stop the loading spinner
      }
    };

    fetchDemoStatus();
  }, []);

  // Determine time-based greeting
  const getTimeBasedGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 12) {
      return "Good morning";
    } else if (currentHour >= 12 && currentHour < 18) {
      return "Good afternoon";
    } else if (currentHour >= 18 && currentHour < 22) {
      return "Good evening";
    } else {
      return "Happy late night";
    }
  };

  const greeting = getTimeBasedGreeting();
  const userName = userDetails?.first_name || "";

  const [userInput, setUserInput] = useState("");

  // FIX #42: Pending message for the chat panel
  // Stores message when chat panel is collapsed, dispatched once FloatingChatbot mounts
  const [pendingChatMessage, setPendingChatMessage] = useState(null);

  const installConfig = useClientInstallConfig();
  const installAvailable = clientInstallAvailable(installConfig) && !conversationId;

  const [projectPreviewState, setProjectPreviewState] = useState({
    loading: true,
    hasProjects: false,
  });
  const hasProjects = !projectPreviewState.loading && projectPreviewState.hasProjects;

  const handleProjectPreviewAvailability = useCallback((nextState) => {
    setProjectPreviewState((current) => {
      if (
        current.loading === nextState.loading &&
        current.hasProjects === nextState.hasProjects
      ) {
        return current;
      }
      return nextState;
    });
  }, []);

  // Which way in. A tool's setup link opens install and the homepage idea
  // link opens the idea box. Otherwise first-time users start on install and
  // returning users see their projects with both choices closed.
  const [door, setDoor] = useState(() => {
    const params = new URLSearchParams(search);
    if (params.get("client")) return "building";
    return params.get("entry") === "app" ? "idea" : null;
  });
  const doorChosen = useRef(door !== null);

  useEffect(() => {
    if (doorChosen.current || projectPreviewState.loading) return;
    doorChosen.current = true;
    if (!projectPreviewState.hasProjects) setDoor("building");
  }, [projectPreviewState]);

  const scrollRef = useRef(null);
  const choicesRef = useRef(null);
  const panelsRef = useRef(null);
  const revealOnOpen = useRef(false);
  const [panelsMinHeight, setPanelsMinHeight] = useState(0);

  const chooseDoor = (next) => {
    doorChosen.current = true;
    const isOpen = next === "building"
      ? door === "building" || door === "describe"
      : door === next;
    // Returning users can close a choice again; first-time users keep one open.
    if (isOpen) {
      if (hasProjects) setDoor(null);
      return;
    }
    revealOnOpen.current = true;
    setDoor(next);
  };

  // The panel area is only as tall as needed to keep the reader's view filled.
  // Opening a choice low on the screen scrolls it up so its content shows; the
  // minimum height keeps the page tall enough for that scroll, and for a short
  // panel replacing a tall one, so nothing jumps. Closed, it takes no space.
  useLayoutEffect(() => {
    const scroller = scrollRef.current, choices = choicesRef.current, panels = panelsRef.current;
    if (!door || !scroller || !choices || !panels) {
      revealOnOpen.current = false;
      setPanelsMinHeight(0);
      return undefined;
    }
    const view = scroller.getBoundingClientRect();
    const choicesTop = choices.getBoundingClientRect().top - view.top;
    const panelsTop = panels.getBoundingClientRect().top - view.top;
    const reveal = revealOnOpen.current && panelsTop > scroller.clientHeight * 0.7;
    revealOnOpen.current = false;
    const shift = reveal ? choicesTop - 24 : 0;
    setPanelsMinHeight(Math.max(0, Math.round(scroller.clientHeight - (panelsTop - shift))));
    if (!reveal) return undefined;
    const frame = requestAnimationFrame(() => {
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      scroller.scrollTo({ top: scroller.scrollTop + shift, behavior: reduce ? "auto" : "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [door]);

  // "Describe instead" is the fallback when a tool can't connect, and the
  // only option for already-building users when install isn't offered here.
  const showInstall = door === "building" && installAvailable;
  const composerMode = door === "idea"
    ? "idea"
    : door === "describe" || (door === "building" && installConfig !== null && !installAvailable)
      ? "describe"
      : null;
  const composer = COMPOSER_COPY[composerMode || "idea"];

  const [isTextareaFocused, setIsTextareaFocused] = useState(false);
  const [typedPlaceholder, setTypedPlaceholder] = useState("");

  useEffect(() => {
    if (!composerMode) return undefined;
    const fullText = COMPOSER_COPY[composerMode].prompt;
    setTypedPlaceholder("");
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setTypedPlaceholder(fullText.slice(0, i));
      if (i >= fullText.length) clearInterval(timer);
    }, 14);
    return () => clearInterval(timer);
  }, [composerMode]);

  // FIX #42: Dispatch pending message once chat panel expands and ref is ready
  useEffect(() => {
    if (
      !isChatCollapsed &&
      pendingChatMessage &&
      chatbotRef.current?.handleSend
    ) {
      chatbotRef.current.handleSend(pendingChatMessage);
      setPendingChatMessage(null);
    }
  }, [isChatCollapsed, pendingChatMessage, chatbotRef]);

  // A described product goes to the plan page, which creates the plan from
  // this brief. The launch state is cached so it survives a strict-mode remount.
  const startPlanCreation = async (designChallenge) => {
    if (planSubmitInFlight.current) return;
    planSubmitInFlight.current = true;
    try {
      const allowed = await checkLimit("plans");
      if (!allowed) return;
      const launchState = {
        designChallenge,
        isNewPlan: true,
        initiateStrategyCreation: true,
        sourceFlow: "client_brief_form",
        workflowType: BRIEF_PLAN_WORKFLOW.id,
        selectedTool: BRIEF_PLAN_WORKFLOW,
        isAutonomous: true,
        experienceOption: "autonomous",
      };
      const cacheKey = `workflow_${Date.now()}`;
      workflowLaunchCache.set(cacheKey, launchState);
      navigate("/plan", { state: { ...launchState, _cacheKey: cacheKey } });
    } finally {
      planSubmitInFlight.current = false;
    }
  };

  // An idea opens the chat. With no plan yet, the chat runs Pabs' discovery
  // conversation and offers to create a plan once it has enough.
  const handleSend = () => {
    const message = userInput.trim();
    if (!message) return;
    if (composerMode === "describe") {
      startPlanCreation(message);
      return;
    }
    setIsChatCollapsed(false);
    setPendingChatMessage(message);
    setUserInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      // Shift + Enter = send; Enter alone adds a new line
      e.preventDefault();
      handleSend();
    }
  };

  // Load conversation from URL parameter - simplified for conversation history
  useEffect(() => {
    if (!conversationId) return;

    const loadConversation = async () => {
      try {
        // Just load the conversation into ChatStateManager - EmbeddedChatPanel will display it
        await chatStateManager.loadConversation(conversationId);
        // Expand the chat panel to show the conversation
        setIsChatCollapsed(false);
      } catch (error) {
        console.error("Error loading conversation:", error);
      }
    };

    loadConversation();
  }, [conversationId, setIsChatCollapsed]);

  return (
    <Flex
      direction="row"
      height="100vh"
      bg="#f0f0f0"
      paddingLeft={0}
      overflow="hidden"
      position="relative"
    >
      {!isFetchingDemoStatus && (
        <DemoVideo isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
      )}
      {/* Chat Panel - Left Side (collapsible) */}
      <Box
        width={{
          base: "0px", // Always 0 on mobile — EmbeddedChatPanel overlays as position:fixed
          md: isChatCollapsed ? "0px" : `${leftPanelWidth}px`,
        }}
        height="100vh"
        borderRightRadius={{ base: 0, md: "24px" }}
        transition="width 0.3s ease"
        overflow="hidden"
      >
        <EmbeddedChatPanel
          ref={chatbotRef}
          currentDesignChallenge=""
          onCollapse={(newCollapsedState) => setIsChatCollapsed(newCollapsedState)}
          isNarrativeCollapsed={isChatCollapsed}
          currentPlanId={null}
          currentWorkflowId={null}
          uiConfig={{}}
          executionPlan={[]}
          shouldAutoLaunchWorkflow={false}
          // Brief page context - no plan data
          selectedPrograms={[]}
          shortSummary=""
          goalStatement=""
          problemStatement=""
          targetStatement=""
          objectivesStatement={[]}
          deliverablesStatement={[]}
          metricsStatement={[]}
          stakeholdersCustomersInvolvement={[]}
          finalProgramSequence={[]}
          totalDuration={0}
          handleComponentAction={() => {}}
          isStrategyCreationMode={false}
          setIsStrategyCreationMode={() => {}}
          programsOfWork={[]}
          methods={[]}
          tools={[]}
          deliverables={[]}
        />
      </Box>

      {/* Main Content Area - Right Side */}
      <Box
        ref={scrollRef}
        flex="1"
        display="flex"
        flexDirection="column"
        overflow="auto"
        bg="#f0f0f0"
        pb={{ base: "64px", md: 0 }}
      >
        {/* Fixed Header */}
        <Box position="relative" zIndex={100} bg="#f0f0f0">
          <NavBar />
          {/* Wider than the content column so the greeting and name share one line */}
          <Box maxW="1200px" w="100%" mx="auto" px={{ base: 4, md: 6 }}>
            <Heading
              as="h1"
              fontSize={{ base: "34px", sm: "42px", md: "56px" }}
              lineHeight="1.15"
              mt={{ base: 5, md: 12 }}
              textAlign="center"
              sx={{ textWrap: "balance" }}
            >
              {`${greeting}${userName ? `, ${userName}` : ""}.`}
            </Heading>
          </Box>
        </Box>

        {/* Returning users: their projects come first */}
        <InstallDisclosure open={hasProjects}>
          <BriefProjectsPreview
            embedded
            onAvailabilityChange={handleProjectPreviewAvailability}
          />
        </InstallDisclosure>

        <Box
          flex="1"
          maxW="800px"
          w="100%"
          mx="auto"
          px={{ base: 4, md: 0 }}
          pb={{ base: 10, md: 16 }}
        >
          <Flex
            ref={choicesRef}
            direction="column"
            align={{ base: "stretch", sm: "center" }}
            gap={4}
            mt={{ base: 8, md: hasProjects ? 2 : 12 }}
          >
            <Text
              as="h2"
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight={hasProjects ? "semibold" : "normal"}
              color={hasProjects ? "gray.800" : "gray.600"}
              textAlign="center"
            >
              {hasProjects ? "Any other product to lead?" : "Where is your product at?"}
            </Text>
            <EntryChoice
              value={door === "describe" ? "building" : door}
              onChange={chooseDoor}
            />
          </Flex>

          {/* Install and the message box swap in the same place */}
          <Box
            ref={panelsRef}
            className="brief-entry-panels"
            display="grid"
            alignContent="start"
            minH={`${panelsMinHeight}px`}
            // Grow at once so the reveal scroll has room; ease only the shrink on close.
            transition={door ? "none" : "min-height 240ms cubic-bezier(.16, 1, .3, 1)"}
            sx={{ "& > *": { gridArea: "1 / 1" }, "@media (prefers-reduced-motion: reduce)": { transition: "none" } }}
          >
            <InstallDisclosure open={showInstall}>
              <Box pt={{ base: 10, md: 16 }}>
                {installAvailable && (
                  <BriefClientSetup
                    config={installConfig}
                    onDescribe={() => chooseDoor("describe")}
                  />
                )}
              </Box>
            </InstallDisclosure>
            <InstallDisclosure open={!!composerMode}>
              <Box pt={{ base: 10, md: 16 }}>
                <VStack
                  spacing={0}
                  width="100%"
                  border="1px solid"
                  borderColor="gray.200"
                  style={{ whiteSpace: "pre-wrap" }}
                  borderRadius={{ base: "28px", md: "3xl" }}
                  bg="white"
                  transition="border-color 0.15s ease, box-shadow 0.15s ease"
                  _hover={{ borderColor: "gray.400" }}
                  _focusWithin={{ borderColor: "blue.400", boxShadow: "0 0 0 3px rgba(66,153,225,0.15)" }}
                >
                  <Box position="relative" width="100%">
                    <TextareaAutosize
                      aria-label={composer.label}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={() => setIsTextareaFocused(true)}
                      onBlur={() => setIsTextareaFocused(false)}
                      placeholder=""
                      minRows={4}
                      maxRows={8}
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        resize: "vertical",
                        fontSize: "16px",
                        fontFamily: "inherit",
                        backgroundColor: "transparent",
                        lineHeight: "1.5",
                        padding: "18px 20px 6px 20px",
                        borderRadius: "28px 28px 0 0",
                        color: "inherit",
                      }}
                    />
                    {/* Animated placeholder with blinking cursor */}
                    {!userInput && !isTextareaFocused && (
                      <Box
                        position="absolute"
                        top="18px"
                        left="20px"
                        right="20px"
                        pointerEvents="none"
                        zIndex={1}
                      >
                        <Box
                          fontSize="16px"
                          fontFamily="inherit"
                          color="gray.400"
                          lineHeight="1.5"
                        >
                          <Box
                            as="span"
                            display="inline-block"
                            width="1.5px"
                            height="1em"
                            bg="black"
                            mr="3px"
                            verticalAlign="text-bottom"
                            animation={`${blink} 1.1s step-end infinite`}
                          />
                          {typedPlaceholder}
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Flex
                    width="100%"
                    px={2}
                    py={2}
                    align="center"
                    justify="space-between"
                    gap={{ base: 2, sm: 3 }}
                  >
                    <KnowledgePopover placement="bottom-start" briefStyle />
                    <Button
                      onClick={handleSend}
                      isDisabled={userInput.trim() === ""}
                      aria-label={`${composer.action}${composer.actionSuffix}`}
                      rightIcon={<MdArrowForward />}
                      bg="gray.900"
                      color="white"
                      borderRadius="full"
                      h="44px"
                      px={5}
                      fontSize="sm"
                      fontWeight="semibold"
                      _hover={{ bg: "gray.700" }}
                      _active={{ bg: "gray.800" }}
                    >
                      {composer.action}
                      {composer.actionSuffix && (
                        <Box as="span" display={{ base: "none", sm: "inline" }}>&nbsp;{composer.actionSuffix.trim()}</Box>
                      )}
                    </Button>
                  </Flex>
                </VStack>
                <Text mt={3} mx={2} fontSize="sm" color="gray.600" lineHeight="1.55">
                  {composer.hint}
                </Text>
                {door === "describe" && installAvailable && (
                  <Text mt={2} mx={2} fontSize="sm" color="gray.600">
                    <Box
                      as="button"
                      type="button"
                      onClick={() => setDoor("building")}
                      color="gray.800"
                      fontWeight="medium"
                      textDecoration="underline"
                      textUnderlineOffset="3px"
                    >
                      Back to connecting your tool
                    </Box>
                  </Text>
                )}
              </Box>
            </InstallDisclosure>
          </Box>
        </Box>
      </Box>
    </Flex>
  );
}

export default ClientBriefForm;
